import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Select from '../../components/ui/Select';
import MonthCard from './components/MonthCard';

const YearOverview = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get year from URL params or default to current year
  const urlParams = new URLSearchParams(location?.search);
  const initialYear = parseInt(urlParams?.get('year')) || new Date()?.getFullYear();
  
  const [selectedYear, setSelectedYear] = useState(initialYear);
  
  // Update URL when year changes
  useEffect(() => {
    const newUrl = `/year-overview?year=${selectedYear}`;
    window.history?.replaceState(null, '', newUrl);
  }, [selectedYear]);
  
  // Generate year options for the next 10 years
  const currentYear = new Date()?.getFullYear();
  const yearOptions = Array.from({ length: 10 }, (_, i) => ({
    value: String(currentYear + i),
    label: String(currentYear + i),
    description: currentYear + i === currentYear ? 'Current Year' : ''
  }));
  
  const months = [
    { id: 1, name: 'January', days: 31, color: 'bg-blue-50 border-blue-200' },
    { id: 2, name: 'February', days: isLeapYear(selectedYear) ? 29 : 28, color: 'bg-purple-50 border-purple-200' },
    { id: 3, name: 'March', days: 31, color: 'bg-green-50 border-green-200' },
    { id: 4, name: 'April', days: 30, color: 'bg-yellow-50 border-yellow-200' },
    { id: 5, name: 'May', days: 31, color: 'bg-pink-50 border-pink-200' },
    { id: 6, name: 'June', days: 30, color: 'bg-indigo-50 border-indigo-200' },
    { id: 7, name: 'July', days: 31, color: 'bg-red-50 border-red-200' },
    { id: 8, name: 'August', days: 31, color: 'bg-orange-50 border-orange-200' },
    { id: 9, name: 'September', days: 30, color: 'bg-teal-50 border-teal-200' },
    { id: 10, name: 'October', days: 31, color: 'bg-amber-50 border-amber-200' },
    { id: 11, name: 'November', days: 30, color: 'bg-cyan-50 border-cyan-200' },
    { id: 12, name: 'December', days: 31, color: 'bg-rose-50 border-rose-200' }
  ];
  
  function isLeapYear(year) {
    return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
  }
  
  const handleMonthClick = (monthId) => {
    navigate(`/month-overview?year=${selectedYear}&month=${monthId}`);
  };
  
  const handleYearChange = (year) => {
    setSelectedYear(parseInt(year));
  };
  
  const handleLogout = () => {
    navigate('/');
  };
  
  const goToToday = () => {
    const today = new Date();
    const month = today?.getMonth() + 1;
    setSelectedYear(today?.getFullYear());
    navigate(`/month-overview?year=${today?.getFullYear()}&month=${month}`);
  };
  
  const previousYear = () => {
    setSelectedYear(prev => prev - 1);
  };
  
  const nextYear = () => {
    setSelectedYear(prev => prev + 1);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header onLogout={handleLogout} />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="font-heading text-3xl font-bold text-foreground">
                  Year Overview
                </h1>
                <p className="font-body text-muted-foreground mt-1">
                  Navigate through your annual planner
                </p>
              </div>
              
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="md"
                  onClick={goToToday}
                  iconName="Calendar"
                  iconPosition="left"
                >
                  Today
                </Button>
                
                <Button
                  variant="default"
                  size="md"
                  onClick={() => navigate('/planner-setup-wizard')}
                  iconName="Settings"
                  iconPosition="left"
                >
                  Setup Wizard
                </Button>
              </div>
            </div>
          </div>
          
          {/* Year Selector */}
          <div className="bg-card border border-border rounded-lg p-6 mb-8">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={previousYear}
                  iconName="ChevronLeft"
                />
                
                <div className="w-48">
                  <Select
                    value={String(selectedYear)}
                    onChange={handleYearChange}
                    options={yearOptions}
                    placeholder="Select year"
                  />
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={nextYear}
                  iconName="ChevronRight"
                />
              </div>
              
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Icon name="Info" size={16} />
                <span>Click on any month to view details</span>
              </div>
            </div>
          </div>
          
          {/* Months Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {months?.map((month) => (
              <MonthCard
                key={month?.id}
                month={month}
                year={selectedYear}
                onClick={() => handleMonthClick(month?.id)}
              />
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default YearOverview;